import React from "react";
import {
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  SxProps,
  Tooltip,
} from "@mui/material";
import {
  IItinerary,
  IPaymentMethod,
  PaymentIcon,
  formatPrice,
  useConfig,
} from "@thekilroygroup/shared-ui";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { useDispatch, useSelector } from "react-redux";
import { shoppingCartSelector, setPaymentMethod } from "redux/shoppingCart";
import { useTranslations } from "hooks/useTranslations";

interface IProps {
  itinerary: IItinerary;
}

export const PaymentRadioGroup = (props: IProps) => {
  const { itinerary } = props;
  const shoppingCart = useSelector(shoppingCartSelector);
  const dispatch = useDispatch();

  const {
    translations: {
      paymentMethod: { paymentTypeInformation, paymentDiscount },
    },
  } = useTranslations();
  const { locale } = useConfig();

  const handleRadioChange = (event: React.FormEvent<HTMLInputElement>) => {
    const matchedPaymentMethod = itinerary?.paymentMethods.find(
      (paymentMethod: IPaymentMethod) => {
        return paymentMethod.method === event.currentTarget.value;
      }
    );
    if (matchedPaymentMethod) {
      dispatch(setPaymentMethod(matchedPaymentMethod));
    }
  };

  const getDiscount = (paymentMethod: IPaymentMethod) => {
    const hasDiscount = paymentMethod.discount?.amount;
    return (
      <Typography
        variant="titleSmall"
        color={hasDiscount ? "secondary.main" : "grey.600"}
      >
        {paymentMethod.discount?.amount
          ? paymentDiscount.discountText.replaceAll(
              "#discount#",
              formatPrice(
                paymentMethod.discount.amount,
                locale,
                paymentMethod.discount.currency
              )
            )
          : paymentDiscount.noFeeText}
      </Typography>
    );
  };

  const getTooltip = (paymentMethod: IPaymentMethod) => {
    return (
      <Tooltip
        title={
          paymentTypeInformation[paymentMethod.method.toLowerCase()] as string
        }
      >
        <InfoOutlined fontSize="small" />
      </Tooltip>
    );
  };

  return (
    <Box sx={sx.radioGroup}>
      <RadioGroup
        aria-labelledby="payment-method-selector"
        name="payment-method-selector"
      >
        {itinerary?.paymentMethods.map(
          (paymentMethod: IPaymentMethod, index: number) => {
            // TODO TEMP FIX - REMOVE WHEN SHARED-UI IS UPDATED
            const iconKeyTemp = paymentMethod.adyenCodes[0]
              .toLowerCase()
              .includes("klarna")
              ? "klarna_account"
              : paymentMethod.adyenCodes[0];

            return (
              <FormControlLabel
                key={index}
                value={paymentMethod.method}
                checked={
                  paymentMethod.method ===
                  shoppingCart.selectedPaymentMethod?.method
                }
                control={
                  <Radio onChange={handleRadioChange} sx={{ paddingLeft: 0 }} />
                }
                sx={sx.label}
                label={
                  <Box sx={sx.radioLabel}>
                    <PaymentIcon iconKey={iconKeyTemp} />
                    <Box>
                      <Typography variant="bodyMedium" sx={sx.method}>
                        {paymentMethod.method}
                        {getTooltip(paymentMethod)}
                      </Typography>
                      {getDiscount(paymentMethod)}
                    </Box>
                  </Box>
                }
              />
            );
          }
        )}
      </RadioGroup>
    </Box>
  );
};

const sx: { [key: string]: SxProps } = {
  radioGroup: {
    background: "white.main",
    width: { xs: "100%", md: `calc(50% - 16px` },
    p: { md: 0 },
  },
  method: {
    flexGrow: 1,
    display: "flex",
    alignItems: "start",
    "& svg": {
      ml: 0.25,
    },
  },
  radioLabel: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    flexDirection: { sm: "row-reverse" },
    justifyContent: { sm: "space-between" },
  },
  label: {
    py: 1,
    px: { xs: 2, md: 0 },
    m: 0,
    ml: 0,
    borderBottomWidth: 1,
    borderBottomStyle: "solid",
    borderBottomColor: "grey.300",
    "&:last-child": {
      borderBottomWidth: 0,
    },
    "& > span:last-child": {
      flexGrow: 1,
    },
  },
};
